import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';
import { Activity, CheckCircle, AlertTriangle, XCircle, RefreshCw } from 'lucide-react';

export default function AdminStatus() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/dashboard');
      return;
    }
    loadServices();
  }, [isSuperuser, navigate]);

  const loadServices = () => {
    setLoading(true);
    api.getStatusServices()
      .then(setServices)
      .catch(err => console.error('Failed to load services:', err))
      .finally(() => setLoading(false));
  };

  const handleStatusChange = async (serviceName, newStatus) => {
    setUpdating(serviceName);
    try {
      const updated = await api.updateServiceStatus(serviceName, { status: newStatus });
      setServices(prev => prev.map(s => s.name === serviceName ? updated : s));
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdating(null);
    }
  };

  if (!isSuperuser) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return <CheckCircle size={16} className="text-emerald-500" />;
      case 'degraded': return <AlertTriangle size={16} className="text-amber-500" />;
      case 'outage': return <XCircle size={16} className="text-red-500" />;
      default: return null;
    }
  };

  const overallStatus = services.some(s => s.status === 'outage') ? 'outage'
    : services.some(s => s.status === 'degraded') ? 'degraded' : 'operational';

  return (
    <div data-testid="admin-status" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="text-cyan-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-white font-mono">System Status</h1>
            <p className="text-sm text-zinc-500">Monitor and manage service health</p>
          </div>
        </div>
        <button
          onClick={loadServices}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-all font-mono text-sm"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Overall Status */}
      <div className={`border rounded-sm p-4 ${
        overallStatus === 'operational' ? 'bg-emerald-500/10 border-emerald-500/30' :
        overallStatus === 'degraded' ? 'bg-amber-500/10 border-amber-500/30' :
        'bg-red-500/10 border-red-500/30'
      }`}>
        <div className="flex items-center gap-3">
          {getStatusIcon(overallStatus)}
          <div>
            <p className="text-white font-mono font-medium capitalize">{overallStatus === 'operational' ? 'All Systems Operational' : overallStatus === 'degraded' ? 'Partial Degradation' : 'System Outage'}</p>
            <p className="text-xs text-zinc-400">Last updated: {new Date().toISOString()}</p>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm">
        <div className="p-4 border-b border-zinc-800">
          <h2 className="text-white font-mono font-medium">Services</h2>
        </div>
        <div className="divide-y divide-zinc-800">
          {loading ? (
            [1,2,3,4].map(i => (
              <div key={i} className="p-4 animate-pulse">
                <div className="h-4 bg-zinc-800 rounded w-1/4"></div>
              </div>
            ))
          ) : services.map(service => (
            <div key={service.name} className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(service.status)}
                <div>
                  <p className="text-white font-mono">{service.name}</p>
                  <p className="text-xs text-zinc-500">{service.uptime}% uptime</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={service.status}
                  onChange={(e) => handleStatusChange(service.name, e.target.value)}
                  disabled={updating === service.name}
                  className="bg-zinc-900 border border-zinc-700 text-zinc-300 rounded-sm px-3 py-1.5 text-sm font-mono focus:border-emerald-500 outline-none"
                  data-testid={`status-select-${service.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <option value="operational">Operational</option>
                  <option value="degraded">Degraded</option>
                  <option value="outage">Outage</option>
                </select>
                {updating === service.name && <RefreshCw size={14} className="text-emerald-500 animate-spin" />}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
